/**
 * FMA Welcome Page - Simple landing page for free FMA Basic users
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/global/auth/AuthContext";

export default function FMAWelcomePage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    // If user is already logged in, show welcome
    // Otherwise, redirect to login after brief delay
    if (!loading && !user) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user, loading, navigate]);

  const handleGetStarted = () => {
    if (user) {
      // If logged in, go to FMA home
      navigate("/fma");
    } else {
      // If not logged in, go to login
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="pt-12 pb-12 text-center space-y-6">
          {/* Logo/Icon */}
          <div className="inline-flex items-center justify-center h-20 w-20 bg-primary/10 rounded-full mb-4">
            <Sparkles className="h-10 w-10 text-primary" />
          </div>

          {/* Heading */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold tracking-tight">
              Welcome to Find My Agency
            </h1>
            <p className="text-xl text-muted-foreground">
              Your free account is ready!
            </p>
          </div>

          {/* Description */}
          <div className="max-w-lg mx-auto space-y-4 text-muted-foreground">
            <p>
              You now have access to our platform to connect with insurance
              agents and agencies across the country.
            </p>
            <p>
              <strong className="text-foreground">What you can do:</strong>
            </p>
            <ul className="text-left space-y-2 max-w-md mx-auto">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Search and browse insurance agents</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Create your agency profile</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Connect with potential team members</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Access community resources</span>
              </li>
            </ul>
          </div>

          {/* CTA */}
          <div className="pt-6">
            {user ? (
              <Button onClick={handleGetStarted} size="lg" className="px-8">
                Go to Dashboard <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            ) : (
              <div className="space-y-3">
                <Button onClick={handleGetStarted} size="lg" className="px-8">
                  Log In to Get Started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="text-sm text-muted-foreground">
                  Check your email for login instructions
                </p>
              </div>
            )}
          </div>

          {/* Upgrade Prompt */}
          <div className="pt-8 border-t">
            <p className="text-sm text-muted-foreground mb-3">
              Want access to advanced features and our powerful CRM?
            </p>
            <Button
              variant="outline"
              onClick={() => navigate("/pricing")}
              size="sm"
            >
              View Upgrade Options
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
